import React, { useEffect, useState } from "react";
import { CallClient } from "@azure/communication-calling";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import {
  addOngoingCall,
  completeOngpingCall,
  getPendingOutgoingCall,
} from "../../../services/agentModule/outgoingcall/outgoingCallService";
import { OUTGOINGCALL_ERRORS, VALIDATION_MESSAGES } from "../../../common/constants/agentModule/outgoingcallConstants";
import { UI_CONFIG } from "../../../common/constants/agentModule/incomingCallsConstants";
import { DISPLAY, STATUS } from "../../../common/constants/commonConstants";
import ProgressOverlay from "../../../common/components/ProgressOverlay";
import SearchCustomerOnCallModel from "./SearchCustomerOnCall";
import SearchCustomerModel from "../incoming/SearchCustomerModel";

const ALTERNATE_CALLER_ID = "+18335269779";

const sampleCustomers = [
  { id: "c1", name: "Alice Johnson", phone: "2021111111" },
  { id: "c2", name: "Bob Smith", phone: "5559876543" },
  //{ id: "c3", name: "Charlie Brown", phone: "5553332211" },
];

const OutgoingCalls = ({
  onCustomerGuid,
  refreshTrigger,
  openSearchCustomer,
  onSearchModelClose,
}) => {
  const [callAgent, setCallAgent] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [activeCustomerId, setActiveCustomerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [outgoingCallCustomers, setOutgoingCallCustomers] = useState([]);
  const [selectedCustomeId, setSelectedCustomerId] = useState(null);
  const [selectedOutgoingCallCustomer, setSelectedOutgoingcallCustomer] =
    useState(null);
 const [successMessage, setSuccessMessage] = useState(null);
  const phonePrefix = "+1";

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem("ACSIdentityToken");
      if (!storedToken) {
        console.error("Missing ACSIdentityToken in localStorage.");
        return;
      }

      try {
        const client = new CallClient();
        const credential = new AzureCommunicationTokenCredential(storedToken);
        const agent = await client.createCallAgent(credential);
        setCallAgent(agent);
      } catch (err) {
        console.error("Failed to initialize call agent:", err);
      }
    };

    init();
  }, []);

  useEffect(() => {
    fetchPendingOutgoingCall();
  }, [refreshTrigger]);

  const fetchPendingOutgoingCall = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPendingOutgoingCall(
        "OutboundCallWithAgent",
        "pending"
      );
      setOutgoingCallCustomers(data || []);
    } catch (err) {
      console.error(OUTGOINGCALL_ERRORS.FETCH_PENDING_OUTGOINGCALL_ERROR, err);
      setError(OUTGOINGCALL_ERRORS.FETCH_PENDING_OUTGOINGCALL_ERROR);
      setOutgoingCallCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onSearchCustomer = async (customerguid, phoneNumber) => {
    onSearchModelClose();
    setIsLoading(true);
    setError(null);

    const addCalldetails = {
      customerguid: customerguid,
      status: "pending",
      userguid: localStorage.getItem("userguid"),
      jobid: `jb-calloutbound-${genrateRandomNumber()}`,
      customeracsid: `+1${phoneNumber}`, //`phoneNumber, // Random 10-digit number
      callconnectionid: `calloutbound-${genrateRandomNumber()}`,
      connectionType: "OutboundCallWithAgent",
    };
    try {
      console.log("addCalldetails", addCalldetails);
      await addOngoingCall(addCalldetails);
      setSuccessMessage(VALIDATION_MESSAGES.CALL_ADDED_SUCCESS);
      fetchPendingOutgoingCall();
    } catch (err) {
      console.error(OUTGOINGCALL_ERRORS.ADD_ONGOING_CALL_ERROR, err);
      setError(OUTGOINGCALL_ERRORS.ADD_ONGOING_CALL_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

   useEffect(() => {
      if (successMessage) {
        const timer = setTimeout(() => setSuccessMessage(""), 3000);
        return () => clearTimeout(timer);
      }
    }, [successMessage]);

  const genrateRandomNumber = () => {
    return Math.floor(1000000000 + Math.random() * 9000000000);
  };

  const formatDisplayNumber = (num) => {
    if (!num) return "";

    const raw = String(num).trim();
    const hadPlus = raw.startsWith("+");
    const digits = raw.replace(/\D/g, ""); // strip everything but digits

    // +1XXXXXXXXXX (E.164) → +1 (AAA) BBB-CCCC
    if (digits.length === 11 && digits[0] === "1") {
      const area = digits.slice(1, 4);
      const central = digits.slice(4, 7);
      const line = digits.slice(7);
      return `+1 (${area}) ${central}-${line}`;
    }

    // 10 digits → (AAA) BBB-CCCC
    if (digits.length === 10) {
      const area = digits.slice(0, 3);
      const central = digits.slice(3, 6);
      const line = digits.slice(6);
      return `(${area}) ${central}-${line}`;
    }

    // Fallbacks: partial input or non-US lengths
    if (digits.length > 6)
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return hadPlus ? `+${digits}` : digits;
  };

  const startCall = async (customer) => {
    if (!callAgent || currentCall) return;

    const fullNumber = `${customer.customeracsid}`;
    try {
      const call = callAgent.startCall([{ phoneNumber: fullNumber }], {
        alternateCallerId: { phoneNumber: ALTERNATE_CALLER_ID },
      });
      setCurrentCall(call);
      setActiveCustomerId(customer.id);
    } catch (err) {
      console.error("Error starting call:", err);
    }
  };

  const hangUp = async () => {
    if (currentCall) {
      try {
        await currentCall.hangUp({ forEveryone: true });
      } catch (err) {
        console.error("Error hanging up:", err);
      }
      setCurrentCall(null);
      setActiveCustomerId(null);
    }
  };

  const handleOutgoingCallCustomerClick = (
    customerGuid,
    jobid,
    callconnectionid
  ) => {
    setSelectedOutgoingcallCustomer(customerGuid);
    setSelectedCustomerId(jobid);
    onCustomerGuid(customerGuid, jobid, callconnectionid);
  };

  const handleComplete = async (customer) => {
    setIsLoading(true);
    try {
      await completeOngpingCall('complete', customer.id)
      setSuccessMessage(VALIDATION_MESSAGES.CALL_COMPLETED_SUCCESS);
      fetchPendingOutgoingCall();
    } catch (error) {
      console.error(OUTGOINGCALL_ERRORS.COMPLETE_ONGOING_CALL_ERROR, error);
      setError(OUTGOINGCALL_ERRORS.COMPLETE_ONGOING_CALL_ERROR);
    } finally {
      setIsLoading(false);
    }

  };
  return (
    <Box
      sx={{
        display: DISPLAY.FLEX,
        gap: UI_CONFIG.GAP,
        flexDirection: "column",
        mb: UI_CONFIG.MARGIN_BOTTOM,
      }}
    >
      <Box
        sx={{ display: DISPLAY.FLEX, gap: UI_CONFIG.GAP, alignItems: "center" }}
      >
        <Typography mb={UI_CONFIG.MARGIN_BOTTOM}>Call Panel</Typography>
      </Box>

      {successMessage && (
        <Alert severity={STATUS.SUCCESS} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
          {error}
        </Alert>
      )}

      {isLoading && <ProgressOverlay />}
      <Stack spacing={2}>
        {outgoingCallCustomers.map((customer) => {
          const isActive = activeCustomerId === customer.jobid;
          return (
            <Card
              key={customer.jobid}
              sx={{
                mb: 2,
                cursor: "pointer",
                border:
                  selectedCustomeId === customer.jobid
                    ? "2px solid #1976d2"
                    : "1px solid #ccc",
                backgroundColor:
                  selectedCustomeId === customer.jobid ? "#e3f2fd" : "#fff",
              }}
              onClick={() =>
                handleOutgoingCallCustomerClick(
                  customer.customerguid,
                  customer.jobid,
                  customer.callconnectionid
                )
              }
            >
              <CardContent>
                <Typography variant="subtitle1">
                  {customer.name}
                  <Chip
                    icon={<LocalPhoneIcon />}
                    label={formatDisplayNumber(customer.customeracsid)}
                    size="small"
                    sx={{ my: 1, ml: "5px" }}
                  />
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!!currentCall && !isActive}
                    onClick={() => startCall(customer)}
                  >
                    Call
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    //disabled={!isActive}
                    onClick={hangUp}
                  >
                    Hang Up
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleComplete(customer)}
                  >
                    Complete
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <SearchCustomerModel
      open={openSearchCustomer}
      onClose={onSearchModelClose}
      onCustomerGuid={onSearchCustomer}
      />
    </Box>
  );
};
export default OutgoingCalls;
